import { REFRESH_TOKEN_KEY } from '@shared/constants'
import { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios'

import {
  getLocalStorageTokens,
  getTokenFromUrl,
  refresh,
  setSsoRefreshUrl,
  StoredTokens
} from './getToken'
import { expandCommaParamsInUrl, splitComma } from './transform-params'
import { isValidURL } from './url'

export type ConfigInterceptorOptions = {
  expandCommaParams?: boolean
  /**
   * Full SSO refresh endpoint URL (e.g. `${VITE_APP_SSO_API}/authorization/token/refresh/`).
   * Passed from the app layer so the shared lib needs no build-tool/env coupling.
   */
  ssoRefreshUrl?: string
}

// Single-flight refresh shared across ALL axios instances/apps in this bundle.
// With short-lived access tokens many requests 401 at once; serializing the
// refresh prevents N parallel calls — and, under SSO rotation, prevents the
// first rotation from blacklisting the token the others are about to use.
let inFlightRefresh: Promise<StoredTokens | null> | null = null

const runSingleFlightRefresh = (
  refreshToken: string
): Promise<StoredTokens | null> => {
  if (inFlightRefresh) {
    console.debug('[auth] refresh single-flight: joined in-flight request')
    return inFlightRefresh
  }

  inFlightRefresh = refresh(refreshToken)
    .catch((error: AxiosError) => {
      console.warn(
        '[auth] token refresh failed',
        error?.response?.status ?? error?.message
      )
      return null
    })
    .finally(() => {
      inFlightRefresh = null
    })

  return inFlightRefresh
}

const handleSessionExpired = () => {
  console.warn('[auth] session expired, logging out')
  localStorage.clear()
  globalThis.location.reload()
}

export const getConfigInterceptors = (
  axios: AxiosInstance,
  useSaasAppToken = true,
  appToken?: () => string | null,
  options: ConfigInterceptorOptions = {}
) => {
  if (options.ssoRefreshUrl) {
    setSsoRefreshUrl(options.ssoRefreshUrl)
  }

  axios.interceptors.request.use(
    (request: InternalAxiosRequestConfig & { skipAuthHeader?: boolean }) => {
      const { access } = getLocalStorageTokens()
      const saasAppToken = appToken?.() || getTokenFromUrl()

      if (options.expandCommaParams) {
        if (request.params && typeof request.params === 'object') {
          const next = {
            ...request.params
          }
          for (const [key, value] of Object.entries(next))
            next[key] = splitComma(key, value)
          request.params = next
        }

        let fullUrl = request.url ?? ''
        if (request.baseURL && request.url && !isValidURL(request.url)) {
          fullUrl = new URL(request.url, request.baseURL).toString()
        }

        fullUrl = expandCommaParamsInUrl(fullUrl)
        if (request.baseURL && request.url && !isValidURL(request.url)) {
          try {
            const abs = new URL(fullUrl)
            request.url = abs.pathname + (abs.search || '') + (abs.hash || '')
          } catch {
            request.url = fullUrl
          }
        } else {
          request.url = fullUrl
        }
      }

      request.headers['Accept-Language'] = localStorage.getItem('storedLng')

      if (access && !request.skipAuthHeader) {
        request.headers['Authorization'] = `Token ${access}`
      }

      if (saasAppToken && useSaasAppToken) {
        request.headers['Saas-App-Token'] = saasAppToken
      }

      return request
    },
    (error) => Promise.reject(error)
  )

  axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & {
        _retry?: boolean
      }

      // Refresh on access-token expiry (HTTP 401). The SSO returns
      // 401 { code: 'token_not_valid' } when the access token is expired.
      // 403 means authorization (not expiry) — refreshing would not help.
      if (
        error.response?.status === 401 &&
        originalRequest &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true

        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
        if (!refreshToken) {
          handleSessionExpired()
          throw error
        }

        // refresh() persists the rotated access AND refresh (guarded); single-flight
        // collapses concurrent 401s into one refresh call.
        const tokens = await runSingleFlightRefresh(refreshToken)
        if (tokens?.access) {
          console.debug(
            '[auth] retrying request after refresh',
            originalRequest.url
          )
          originalRequest.headers['Authorization'] = `Token ${tokens.access}`
          return axios(originalRequest)
        }

        handleSessionExpired()
        throw error
      }

      throw error
    }
  )
}
